import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HoverZoomImage from "../../components/HoverZoomImage";
import { buildPdfEmbedUrl, isPdfAssetUrl } from "./detailsDialogUtils";

function isSvgSource(source) {
  if (typeof source !== "string" || !source) {
    return false;
  }

  if (source.startsWith("data:image/svg+xml")) {
    return true;
  }

  return /\.svg($|[?#])/i.test(source);
}

function DetailsMediaPanel(props) {
  const { entry, images, activeIndex, setActiveIndex } = props;

  const hasImages = images.length > 0;
  const safeIndex = hasImages
    ? Math.min(Math.max(activeIndex, 0), images.length - 1)
    : 0;
  const activeImage = hasImages ? images[safeIndex] : null;
  const activeImageIsPdf = isPdfAssetUrl(activeImage?.url);

  const goPrev = () => {
    if (!hasImages) return;
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goNext = () => {
    if (!hasImages) return;
    setActiveIndex((prev) => (prev + 1) % images.length);
  };

  return (
    <Box className="browse-designs-details-media-layout">
      <Box className="browse-designs-details-main-image-wrap">
        {activeImage && !activeImageIsPdf ? (
          <HoverZoomImage
            src={activeImage.url}
            alt={activeImage.alt || entry?.name || "Design image"}
            imgProps={{ className: "browse-designs-details-main-image" }}
            containerSx={{
              width: "100%",
              maxWidth: "100%",
              maxHeight: "52vh",
            }}
            imageSx={{
              width: "100%",
              maxHeight: "52vh",
              objectFit: "contain",
              backgroundColor: "#fff",
            }}
            previewSize={380}
            borderRadius={18}
          />
        ) : activeImage && activeImageIsPdf && activeImage.previewUrl ? (
          <HoverZoomImage
            src={activeImage.previewUrl}
            alt={activeImage.alt || `${entry?.name || "Design"} PDF preview`}
            imgProps={{ className: "browse-designs-details-main-image" }}
            containerSx={{
              width: "100%",
              maxWidth: "100%",
              maxHeight: "52vh",
            }}
            imageSx={{
              width: "100%",
              maxHeight: "52vh",
              objectFit: "contain",
              backgroundColor: "#fff",
            }}
            previewSize={380}
            borderRadius={18}
          />
        ) : activeImage && activeImageIsPdf ? (
          <Box className="browse-designs-details-main-pdf-wrap">
            <object
              data={buildPdfEmbedUrl(activeImage.url)}
              type="application/pdf"
              className="browse-designs-details-main-pdf"
            >
              <a href={activeImage.url} target="_blank" rel="noopener noreferrer">
                Open PDF
              </a>
            </object>
          </Box>
        ) : (
          <Box className="browse-designs-details-main-image-placeholder">No Images</Box>
        )}
      </Box>

      <Box className="browse-designs-details-carousel-col">
        <Box className="browse-designs-details-image-toolbar">
          <IconButton
            size="small"
            onClick={goPrev}
            disabled={!hasImages || images.length < 2}
            aria-label="Previous image"
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" className="browse-designs-details-image-count">
            {hasImages ? `${safeIndex + 1} / ${images.length}` : "0 / 0"}
          </Typography>
          <IconButton
            size="small"
            onClick={goNext}
            disabled={!hasImages || images.length < 2}
            aria-label="Next image"
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box className="browse-designs-details-thumbs-col">
          {hasImages ? (
            images.map((image, idx) => (
              <button
                key={`${entry?._id || "entry"}-thumb-${idx}`}
                type="button"
                className={`browse-designs-details-thumb-btn${
                  idx === safeIndex ? " is-active" : ""
                }`}
                onClick={() => setActiveIndex(idx)}
                aria-label={`Select image ${idx + 1}`}
              >
                {isPdfAssetUrl(image.url) && image.previewUrl ? (
                  <img
                    src={image.previewUrl}
                    alt={image.alt || `${entry?.name || "Design"} PDF thumbnail ${idx + 1}`}
                    className="browse-designs-details-thumb-image"
                  />
                ) : isPdfAssetUrl(image.url) ? (
                  <Box className="browse-designs-details-thumb-pdf">PDF</Box>
                ) : (
                  <img
                    src={image.url}
                    alt={image.alt || `${entry?.name || "Design"} thumbnail ${idx + 1}`}
                    className="browse-designs-details-thumb-image"
                    style={{
                      backgroundColor: isSvgSource(image.url) ? "#ffffff" : undefined,
                    }}
                  />
                )}
              </button>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No thumbnails
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default DetailsMediaPanel;
